import { Check, CircleAlert } from "lucide-react";
import { Toaster as Sonner, type ToasterProps } from "sonner";
import { buttonVariants } from "@shared/components/ui/button";
import { cn } from "@shared/lib/utils";

/**
 * Bouton d'un toast (« Ouvrir », « Réessayer ») : un bouton secondaire
 * compact. Exporté pour qu'une action rendue en lien (`<Link>`) ait la même
 * allure que celles que sonner rend lui-même.
 */
export const toastActionClassName = cn(
  buttonVariants({ variant: "outline", size: "sm" }),
  "h-7 flex-none px-2.5 text-[12.5px]",
);

/**
 * Notifications shadcn/ui (sonner), réservées aux actions coûteuses ou longues
 * qui se dénouent hors de la vue (docs/DESIGN_SYSTEM.md §6.5) — jamais pour
 * une action instantanée et réversible. Thème clair uniquement, comme le reste
 * du produit : surface blanche, filet, rayon 8 px, Geist 13 px, la seule ombre
 * admise hors menus et dialogues, une icône grise au plus. Sonner ne fournit
 * que la mécanique (pile, minuterie, balayage) : `unstyled`, et le style vient
 * des jetons.
 *
 * En bas à droite ; sous 600 px, sur toute la largeur moins la gouttière de
 * 16 px. Le bas se règle par `--toast-bottom` sur le conteneur (la barre
 * d'onglets mobile de l'app le remonte).
 */
function Toaster({ className, toastOptions, ...props }: ToasterProps) {
  return (
    <Sonner
      className={cn("toaster group", className)}
      duration={6000}
      icons={{
        success: <Check aria-hidden="true" />,
        error: <CircleAlert aria-hidden="true" />,
      }}
      mobileOffset={{ bottom: "var(--toast-bottom, 16px)", left: 16, right: 16 }}
      offset={{ bottom: "var(--toast-bottom, 24px)", right: 24 }}
      position="bottom-right"
      theme="light"
      toastOptions={{
        unstyled: true,
        ...toastOptions,
        classNames: {
          // Le body d'un dialogue modal ignore le pointeur : le toast le reprend.
          // Derrière le premier, les toasts empilés ne montrent que leur bord.
          toast:
            "pointer-events-auto flex w-(--width) items-center gap-3 overflow-hidden rounded-lg border bg-popover p-3.5 font-sans text-[13px] leading-snug text-popover-foreground shadow-md data-[front=false]:data-[expanded=false]:*:opacity-0",
          icon: "flex flex-none text-muted-foreground [&_svg]:size-4",
          content: "grid min-w-0 flex-1 gap-0.5",
          title: "font-medium",
          description: "text-muted-foreground",
          actionButton: toastActionClassName,
          cancelButton: cn(buttonVariants({ variant: "ghost", size: "sm" }), "h-7 flex-none px-2.5 text-[12.5px]"),
          ...toastOptions?.classNames,
        },
      }}
      {...props}
    />
  );
}

export { Toaster };
